// `shortlist-extension-alerts/slim-header-dropdown/component/shortlist.slim.header.dropdown.tpl.html`
// — the alert selector at the top of the content view in slim mode (plan §7 phase 3, slice 2).
//
// This IS the "hunter list" the plan's slice 2 names: in FullMode the alerts are a column of
// `extension-alert-tab`s, and in the slim frame — the only mode the extension ever renders in
// — they collapse to this one row, with the current alert's name and a dropdown for the rest.
//
// ⚠️ THE CONDITIONS ARE THE COMPONENT. Almost every element here is behind an `ng-if`, and the
// row a user sees depends on what kind of alert is loaded. Ported as written rather than
// simplified to the common case:
//
//   - `hostSite` set (a LinkedIn/Indeed/… saved search) REPLACES the edit pen and the actions
//     menu with a single save CTA, and adds a footer of extra details. It is the whole reason
//     the pen is behind `!hostSite && !extensionJobId` rather than just rendered.
//   - `extensionJobId` set (an alert bound to an ATS job) hides the pen and the Delete item,
//     because the job owns the criteria.
//   - a company search with a logo gets an image before the name.
//
// What is deliberately absent: `shortlist-friendly-criteria` (the criteria summary tooltip)
// and the dropdown MENU's own panel. Both are their own components with their own templates,
// and the menu needs the alert list plus the ATS-filters and duplicate/delete operations —
// slice 2's second half. The trigger renders because it is in every reference shot of these
// routes; opening it is inert until then, the same trade the header CTAs document.
import type { ReactElement, ReactNode } from 'react'
import { slimHeaderControls } from './slimHeaderControls.ts'
import { uibDropdown } from '../common/uibDropdown.ts'

export type { SlimHeaderAlert } from './slimHeaderControls.ts'
import type { SlimHeaderAlert } from './slimHeaderControls.ts'

export interface SlimHeaderDropdownProps {
  alert: SlimHeaderAlert
  /** `ctrl.isCompanySearch` — gates the logo, which only a company search has. */
  isCompanySearch?: boolean
  /**
   * `!!$state.current.data.isCheckboxDependent` — true while a bulk operation owns the frame,
   * which disables the row and explains itself in the title attribute.
   */
  checkboxDependent?: boolean
  /** `data.checkboxOperationLabel`, named in that title. */
  checkboxOperationLabel?: string
  /** `totalNewCandidatesForExtension > 0` — the unread dot on the actions caret. */
  hasNewCandidates?: boolean
  onEdit?: () => void
  /**
   * `slim-header-dropdown-tooltip` ON THE NAME — the alert list (`panel` below).
   *
   * ⚠️ THE NAME AND THE ☰ ARE DIFFERENT MENUS, and this port had them crossed. The template
   * puts the tooltip directive on `.shortlist-header-slim-alert-name` and a separate
   * `uib-dropdown` on `.more-options-caret`: clicking the NAME lists the searches you can
   * switch to, clicking the BARS offers Search criteria / Filters / Duplicate / Delete.
   * Wiring the alert list to the bars left the name — the control carrying `dropdown-enabled`
   * and its own ▾ caret — doing nothing at all.
   */
  onToggleAlertList?: () => void
  /** `uib-dropdown-toggle` on the ☰ — the four-item actions menu, not the alert list. */
  onToggleActions?: () => void
  /** `ctrl.openAtsFilters()`, behind `isHasAtsFilters && !isCriteriaAtsFiltersEnabled`. */
  onAtsFilters?: () => void
  /**
   * `ctrl.isHasAtsFilters && !ctrl.isCriteriaAtsFiltersEnabled` — and the second half is the
   * literal `true` upstream, so this is FALSE in production. See the menu body.
   */
  showAtsFilters?: boolean
  /**
   * `ctrl.isBeautifiedSearchCriteriaEnabled` — `!$stateParams.externalSource` on the candidate
   * product and hardcoded FALSE on contacts (`setBeautifiedSearchCriteriaEnabled`), which is
   * why the contacts menu has three items where the candidates' has four.
   */
  showSearchCriteria?: boolean
  /** `openBeautifiedSearchCriteriaFromDropdown()` — opens the friendly-criteria panel. */
  onSearchCriteria?: () => void
  /** `duplicateAlertFromSlimHeader()` — the edit screen on a NEW alert seeded from this one. */
  onDuplicate?: () => void
  /** `deleteAlertFromSlimHeader()` — confirm first; see the caller. */
  onDelete?: () => void
  /** `ctrl.slimHeaderDropdownTooltipOpen` for the ☰'s own menu. */
  actionsOpen?: boolean
  /** The host-site save CTA, which replaces the edit pen for an imported search. */
  onSaveHostSearch?: () => void
  /** `ctrl.alertHostSiteExtraDetails` — the footer rows, host-site alerts only. */
  hostSiteExtraDetails?: readonly string[]
  /**
   * The dropdown's contents, rendered INSIDE this component when open.
   *
   * ⚠️ IT HAS TO BE A CHILD, NOT A SIBLING, and that is the whole bug it fixes. Upstream
   * attaches the list with `uib-tooltip` on THIS element — `tooltipAppendToBody` is
   * commented out — so the tooltip is absolutely positioned against
   * `.shortlist-header-slim-alert-name-hover-ctas`, which is `position: relative` for exactly
   * that reason. Rendered as a sibling further down the column, as this port had it, the
   * panel is a plain block in flow: it PUSHES the source row, the automation banner and the
   * whole candidate list down the page instead of overlaying them.
   */
  panel?: ReactNode
  /** `ctrl.slimHeaderDropdownTooltipOpen` — the alert list is showing. */
  alertListOpen?: boolean
  /** `shortlist-friendly-criteria` — the ☰ menu's "Search criteria" panel, when open. */
  criteriaPanel?: ReactNode
  /**
   * `automatedHuntersEnabled && extensionProduct === Candidates` — see the wrap below. It is
   * the same expression `SlimHeaderDropdownPanel` takes, and it has to be here too because the
   * class goes on the WRAP, which that component does not own.
   */
  automatedHunters?: boolean
}

export function SlimHeaderDropdown({
  alert,
  isCompanySearch = false,
  checkboxDependent = false,
  checkboxOperationLabel,
  hasNewCandidates = false,
  onEdit,
  onToggleAlertList,
  onToggleActions,
  onAtsFilters,
  showAtsFilters = false,
  showSearchCriteria = false,
  onSearchCriteria,
  onDuplicate,
  onDelete,
  actionsOpen = false,
  onSaveHostSearch,
  panel,
  criteriaPanel,
  automatedHunters = false,
  hostSiteExtraDetails = [],
  alertListOpen = false,
}: SlimHeaderDropdownProps): ReactElement {
  const { isHostSearch, showsEdit, showsActions, showsSaveCta, showsLogo } = slimHeaderControls(alert, isCompanySearch)
  const logoUrl = alert.metadata?.companyLogoUrl

  return (
    <div
      className={[
        'shortlist-header-slim-alert-name-hover-ctas',
        checkboxDependent ? 'disabled' : 'active',
        isHostSearch ? 'host-search-header' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      // `ng-attr-title` renders NO attribute when the expression is null, which is not the
      // same as `title=""` — an empty title still suppresses a parent's tooltip.
      {...(checkboxDependent
        ? { title: `Please close the ${checkboxOperationLabel ?? ''} window to continue` }
        : {})}
    >
      <div className="shortlist-header-slim-alert-name-container">
        {showsLogo ? (
          <div className="shortlist-header-slim-alert-image-container">
            {/* `ng-scope` is AngularJS's own marker class and carries styling here, so it is
                kept verbatim — the selector in the stylesheet is written against it. */}
            {/* `showsLogo` already proved this is a non-empty string; the coalesce is for
                the type, not for a case that can reach here. */}
            <img className="shortlist-header-slim-alert-image ng-scope" src={logoUrl ?? ''} alt="" />
          </div>
        ) : null}
        {/* ⚠️ `dropdown-enabled` DRAWS THE CARET, and nothing in the template does. The
            stylesheet hangs a `:after` with Font Awesome's `\f0d7` off this class
            (`component/_slim-header-dropdown.scss:106`), so without it the alert name simply
            has no ▾ beside it and nothing says the name is clickable. Upstream sets it from
            `!slimHeaderDropdownTooltipDisable`, which is `!isCompanySearch` — a company search
            has no other alert to switch to. */}
        <div
          className={[
            'shortlist-header-slim-alert-name',
            isCompanySearch ? '' : 'dropdown-enabled',
            alertListOpen ? 'dropdown-opened' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          title={alert.name}
          // `dropdown-enabled` is what draws the ▾; making it clickable is what makes the ▾
          // mean something. Upstream's directive binds the same element.
          {...(isCompanySearch
            ? {}
            : {
                role: 'button',
                tabIndex: 0,
                onClick: onToggleAlertList,
                onKeyDown: (event: React.KeyboardEvent) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  onToggleAlertList?.()
                },
              })}
        >
          <div className="alert-name-truncated truncate">{alert.name}</div>
        </div>
        <div className="slim-view-hunter-options">
          {/* ⚠️ THE "46-PIXEL FLOOR" UNDER EVERY SHORTLIST ROUTE WAS NEVER THIS GLYPH. The
              note that stood here blamed a different Font Awesome face resolving for weight
              900, on the strength of the `<i>` measuring 14x14 at x=158 y=94 on both sides
              with no whole-pixel shift aligning the rasters. The box was measured against our
              own page: the reference has the pen at x=154, and the four pixels are the space
              the template collapses out of the newline below. With it restored the column
              profiles of both icons match exactly, and the route's diff is live data — a
              candidate this port has since opened draws `.is-viewed`, which is `font-weight:
              400` where the capture has 700. */}
          {showsEdit ? (
            <span className="slim-view-on-view-item" title="Edit">
              <i
                className="fas fa-pen"
                role="button"
                aria-label="Edit search"
                tabIndex={0}
                onClick={onEdit}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  onEdit?.()
                }}
              />
            </span>
          ) : null}
          {/* ⚠️ A SPACE, AND IT IS 4 PIXELS OF LAYOUT. The template has a newline between the
              edit span and the caret span, which HTML collapses into one space; these are
              inline elements in a right-aligned strip, so dropping it pulls everything left
              of the caret 4px to the right. That is the whole of this route's 715-pixel diff
              — the pen sat at x=158 where the reference has it at x=154, at every width.
              Upstream's text node survives `ng-if` on either neighbour, so this is an
              expression rather than literal JSX whitespace, which React would strip. */}
          {' '}
          {showsActions ? (
            <span className={uibDropdown('more-options-caret', actionsOpen)}>
              {hasNewCandidates ? <span className="any-alert-has-new" /> : null}
              <i
                className="far fa-bars"
                title="More actions"
                role="button"
                aria-label="More actions"
                tabIndex={0}
                onClick={onToggleActions}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  onToggleActions?.()
                }}
              />
              {/* ⚠️ "Filters" IS DEAD CODE UPSTREAM AND WAS THE ONLY ITEM THIS MENU HAD.
                  Its condition is `isHasAtsFilters && !isCriteriaAtsFiltersEnabled`, and
                  `isCriteriaAtsFiltersEnabled` is assigned the LITERAL `true` with the feature
                  flag commented out beside it (`slim.header.dropdown.component.ng1.ts:125`) —
                  so production never draws it. Measured beside build 354: prod's menu reads
                  "Search criteria · Duplicate · Delete" and this one read "Filters".

                  `showAtsFilters` stays a prop: it is still what gates the item, and the day
                  the flag comes back the caller can pass it. */}
              {actionsOpen ? (
                <ul className="dropdown-menu dropdown-menu-right" role="menu">
                  {showSearchCriteria ? (
                    <li role="menuitem">
                      <a
                        href=""
                        onClick={(event) => {
                          event.preventDefault()
                          onSearchCriteria?.()
                        }}
                      >
                        <i className="fas fa-info-circle" />
                        &nbsp;&nbsp; Search criteria
                      </a>
                    </li>
                  ) : null}
                  {showAtsFilters ? (
                    <li role="menuitem">
                      <a
                        href=""
                        onClick={(event) => {
                          event.preventDefault()
                          onAtsFilters?.()
                        }}
                      >
                        <i className="fas fa-filter" />
                        &nbsp;&nbsp; Filters
                      </a>
                    </li>
                  ) : null}
                  <li role="menuitem">
                    <a
                      href=""
                      onClick={(event) => {
                        event.preventDefault()
                        onDuplicate?.()
                      }}
                    >
                      <i className="fas fa-copy" />
                      &nbsp;&nbsp; Duplicate
                    </a>
                  </li>
                  {/* `ng-if="!alert.extensionJobId"` — an alert that belongs to an ATS job is
                      not the recruiter's to delete. */}
                  {alert.extensionJobId == null ? (
                    <li role="menuitem">
                      <a
                        href=""
                        onClick={(event) => {
                          event.preventDefault()
                          onDelete?.()
                        }}
                      >
                        <i className="fas fa-trash-alt selected-hover-delete-icon" />
                        &nbsp;&nbsp; Delete
                      </a>
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </span>
          ) : null}
          {showsSaveCta ? (
            <span
              className="host-search-result-save"
              role="button"
              aria-label="Save search"
              tabIndex={0}
              onClick={onSaveHostSearch}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onSaveHostSearch?.()
              }}
            >
              <i className="fas fa-pen" />
            </span>
          ) : null}
        </div>
      </div>
      {/* ⚠️ THE TOOLTIP CHROME IS LOAD-BEARING, not decoration. `_slim-header-dropdown-tooltip.scss`
          hangs everything off `.slim-header-dropdown-tooltip-wrap.tooltip` — it is what forces
          `opacity: 1` over bootstrap's `.tooltip { opacity: 0 }`, strips the arrow's border, and
          turns `.tooltip-inner` from a 276px dark centred bubble into a white left-aligned panel
          at the slim width with no padding. Rendering the container bare, as this port did, got
          none of that AND none of the positioning.

          `bottom-right` is upstream's `tooltipPlacement`. The inline `top` stands in for uib's
          positioning service, which computes it from the trigger: 100% of this element's height
          is directly beneath it, which is what "bottom" means here. The wrap's own
          `left: … !important` already fixes the horizontal. */}
      {/* ⚠️ `automated-hunters-enabled` IS A LAYOUT CLASS, NOT A FEATURE MARKER, and leaving
          it off is what made this panel look wrong. The tooltip directive adds it when
          `automatedHuntersEnabled && extensionProduct === Candidates`
          (`slim.header.dropdown.tooltip.directive.ng1.ts:22`), and
          `_slim-header-dropdown-tooltip.scss:79` answers with `left: 0 !important; width: 100%`
          plus `max-width: 100%` on the inner. Without it the wrap keeps the base rule's
          `left: 10px !important` and the inner's slim max-width: measured beside build 354 at
          378px, prod's panel is 199px wide at left 0 and this one was 180px at left 10.

          `bottom` rides with `bottom-right` upstream — uib sets both — and it is what the
          arrow's position keys off. */}
      {panel === undefined ? null : (
        <div
          className={`slim-header-dropdown-tooltip-wrap${
            automatedHunters ? ' automated-hunters-enabled' : ''
          } tooltip bottom bottom-right`}
          style={{ top: '100%' }}
        >
          <div className="tooltip-arrow" />
          <div className="tooltip-inner">{panel}</div>
        </div>
      )}
      {/* ⚠️ A SIBLING OF THE NAME CONTAINER, INSIDE THE HOVER-CTAS DIV, which is where
          upstream's `<div shortlist-friendly-criteria=…>` sits — and the wrap class is the
          SLIM one: `_friendly.criteria.tooltip.scss` gives the full-view container 460px,
          which overflows a 200px panel. `friendly-criteria-tooltip-placement="bottom"` is what
          the slim header passes, so `top: 100%` is the placement. */}
      {criteriaPanel === undefined || criteriaPanel === null ? null : (
        <div className="friendly-criteria-tooltip-wrap-slim tooltip bottom" style={{ top: '100%' }}>
          <div className="tooltip-arrow" />
          <div className="tooltip-inner">{criteriaPanel}</div>
        </div>
      )}
      {isHostSearch && hostSiteExtraDetails.length > 0 ? (
        <div className="host-search-result-footer">
          {hostSiteExtraDetails.map((detail) => (
            <div className="host-search-result-alert-extra-detail" key={detail} title={detail}>
              {detail}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
